import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WorkOrderReturn } from '../clients/rust-api-client';
import { workOrderService } from '../clients/ApiClients';
import { KanbanBoard, KanbanColumnConfig } from '../common/Custom/Kanban';
import { Badge } from '../common/Catalyst/badge';
import ShowAlert from '../ShowAlert';
import { useSyncedWorkOrders } from '../sync/hooks';
import { updateWorkOrder } from '../sync/writes';
import { canEditWorkOrder } from '../constants/UserTypes';
import { getUserTypeFromToken } from '../utils/TokenUtils';
import { apiErrorMessage } from '../utils/apiErrors';

// Status values must match API responses (PascalCase)
type WorkOrderStatus = 'Pending' | 'Approved' | 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled';

const workOrderColumns: KanbanColumnConfig<WorkOrderStatus>[] = [
  { status: 'Pending', label: 'Pending', color: 'yellow' },
  { status: 'Approved', label: 'Approved', color: 'cyan' },
  { status: 'Scheduled', label: 'Scheduled', color: 'purple' },
  { status: 'InProgress', label: 'In Progress', color: 'blue' },
  { status: 'Completed', label: 'Completed', color: 'green' },
  { status: 'Cancelled', label: 'Cancelled', color: 'red' },
];

const priorityColors: Record<string, 'red' | 'orange' | 'yellow' | 'green' | 'zinc'> = {
  urgent: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'green',
};

// Map display status to API update format (PascalCase to match database storage)
const statusToApiFormat: Record<WorkOrderStatus, string> = {
  'Pending': 'Pending',
  'Approved': 'Approved',
  'Scheduled': 'Scheduled',
  'InProgress': 'InProgress',
  'Completed': 'Completed',
  'Cancelled': 'Cancelled',
};

interface WorkOrderKanbanBoardProps {
  hoaId?: string | null;
  homeId?: string | null;
  /** Fill the parent's width and height (the work orders page). */
  fill?: boolean;
  /**
   * A bar over the board, laid out like the list's filter bar, with this
   * at its right end: the view switch, in the same place in both views.
   */
  toolbar?: React.ReactNode;
  /**
   * With `fill`: the toolbar becomes an edge-to-edge top row the same
   * height as the list's filter bar, so the view switch does not move
   * when the view changes, and the columns get their own gutter.
   */
  flush?: boolean;
}

export function WorkOrderKanbanBoard({
  hoaId,
  homeId,
  fill = false,
  toolbar,
  flush = false,
}: WorkOrderKanbanBoardProps): React.ReactElement {
  const navigate = useNavigate();
  const [fetchedWorkOrders, setWorkOrders] = useState<WorkOrderReturn[]>([]);
  // From the sync engine's copy when there is one (#561). A dropped card's
  // new status is laid over it by the write queue until the server echoes
  // it, so the card does not jump back in between.
  const synced = useSyncedWorkOrders({ hoaId, homeId });
  const haveSynced = synced !== null;
  const workOrders = synced ?? fetchedWorkOrders;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const fetchWorkOrders = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        let response;

        if (homeId) {
          response = await workOrderService.getWorkOrdersByHome({ homeId });
        } else if (hoaId) {
          response = await workOrderService.getWorkOrdersByHoa({ hoaId });
        } else {
          response = await workOrderService.getAllWorkOrdersForUser();
        }

        setWorkOrders(response.data.work_orders);
        setError(null);
      } catch (err) {
        console.error('Error fetching work orders:', err);
        setError(apiErrorMessage(err, "Couldn't load work orders."));
        setShowError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkOrders();
  }, [hoaId, homeId, haveSynced]);

  const handleStatusChange = async (workOrderId: string, newStatus: WorkOrderStatus): Promise<void> => {
    const workOrder = workOrders.find((wo) => wo.id === workOrderId);
    if (!workOrder) return;

    try {
      // Convert display status to API format (lowercase with underscores)
      const apiStatus = statusToApiFormat[newStatus];

      const saved = await updateWorkOrder(
        workOrderId,
        { status: apiStatus },
        { status: newStatus }
      );
      // Without a copy, the board shows what it fetched.
      if (saved) {
        setWorkOrders((prev) => prev.map((wo) => (wo.id === workOrderId ? saved : wo)));
      }
    } catch (err) {
      console.error('Error updating work order status:', err);
      setError(apiErrorMessage(err, "Couldn't update the status."));
      setShowError(true);
      throw err; // Re-throw to trigger optimistic update rollback
    }
  };

  const handleCardClick = (workOrderId: string) => {
    navigate(`/work-orders/${workOrderId}`);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: string | null | undefined): string => {
    if (!amount) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(amount));
  };

  const formatLabel = (str: string): string => {
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderWorkOrderCard = (workOrder: WorkOrderReturn, isDragging?: boolean) => (
    <div
      onClick={() => !isDragging && handleCardClick(workOrder.id)}
      className="bg-white dark:bg-zinc-800 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 p-3 hover:shadow-md transition-shadow duration-200 cursor-pointer"
    >
      {/* Title */}
      <h4 className="font-medium text-sm text-zinc-900 dark:text-zinc-100 mb-2 line-clamp-2">
        {workOrder.title}
      </h4>

      {/* Type & Priority */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <Badge color="zinc">{formatLabel(workOrder.work_order_type)}</Badge>
        <Badge color={priorityColors[workOrder.priority.toLowerCase()] || 'zinc'}>
          {formatLabel(workOrder.priority)}
        </Badge>
      </div>

      {/* Description */}
      {workOrder.description && (
        <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-2 line-clamp-2">
          {workOrder.description}
        </p>
      )}

      {/* Footer info */}
      <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400 mt-2 pt-2 border-t border-zinc-100 dark:border-zinc-700">
        {workOrder.scheduled_date && (
          <span>{formatDate(workOrder.scheduled_date)}</span>
        )}
        {workOrder.estimated_cost && (
          <span className="font-medium">{formatCurrency(workOrder.estimated_cost)}</span>
        )}
        {!workOrder.scheduled_date && !workOrder.estimated_cost && (
          <span className="italic">No schedule/cost set</span>
        )}
      </div>

      {/* Vendor/Assigned */}
      {(workOrder.vendor_name || workOrder.assigned_to) && (
        <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
          {workOrder.vendor_name && <span>Vendor: {workOrder.vendor_name}</span>}
          {workOrder.vendor_name && workOrder.assigned_to && ' • '}
          {workOrder.assigned_to && <span>Assigned: {workOrder.assigned_to}</span>}
        </div>
      )}
    </div>
  );

  return (
    <div className={fill ? 'flex h-full min-h-0 flex-col' : undefined}>
      {toolbar && (
        <div
          className={
            flush
              ? 'flex min-h-[3.25rem] items-center justify-end gap-3 border-b border-line/60 px-3 py-2'
              : 'mb-3 flex min-h-[2.75rem] items-center justify-end gap-3 rounded-lg border border-line bg-raised px-3 py-1.5'
          }
        >
          <span className="text-[12px] text-fg-muted" aria-live="polite">
            {workOrders.length}
          </span>
          {toolbar}
        </div>
      )}
      {/* Flush against the panel's edges, the alert needs its own inset. */}
      <div className={flush ? 'px-3 pt-3 empty:hidden' : undefined}>
        <ShowAlert
          alertType="error"
          alertTitle="Error"
          alertMessage={error || 'An error occurred'}
          isVisible={showError}
          onClose={() => setShowError(false)}
          timeout={5000}
          isInline={true}
        />
      </div>
      <div className={flush ? 'flex min-h-0 flex-1 flex-col p-4 lg:p-6' : 'contents'}>
        <KanbanBoard
          items={workOrders}
          columns={workOrderColumns}
          getItemStatus={(item) => item.status as WorkOrderStatus}
          getItemId={(item) => item.id}
          renderCard={renderWorkOrderCard}
          onStatusChange={handleStatusChange}
          // A BoardMember reads work orders but may not move them.
          readOnly={!canEditWorkOrder(getUserTypeFromToken())}
          isLoading={loading && !haveSynced}
          fill={fill}
        />
      </div>
    </div>
  );
}

export default WorkOrderKanbanBoard;
